// LAP-768 — Carte « Conseiller relationnel » du Social Dashboard.
//
// Miroir de la carte Recommandation de la Synthèse Professionnelle (LAP-767) :
//   • un score relationnel RÉEL (note 0-100 calculée par buildSocialAdvisor,
//     mêmes règles que la page Conseiller Social),
//   • la recommandation mise en avant (insight prioritaire du backend),
//   • un accès direct « Voir l'analyse complète » vers /conseiller-social.
//
// Le Social Dashboard n'avait jusqu'ici aucune carte conseiller : cette carte
// devient la porte d'entrée du Conseiller Social / Relationnel, à l'image des
// autres tableaux de bord (pro / écologie / temps).
import React from 'react'
import { Box, Button, Chip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CardHeader from '../../Investments/_shared/CardHeader'
import { buildSocialAdvisor } from '../../ConseillerSocial/utils/buildSocialAdvisor'

const SOCIAL_ACCENT = '#f472b6'

// LAP-768 — Couleur du chip de score, alignée sur les conseillers (≥70 vert,
// ≥45 ambre, sinon rouge ; gris si non évalué).
function scoreChipColors(score) {
  if (score == null) return { bg: 'rgba(148,163,184,0.18)', fg: '#94a3b8' }
  if (score >= 70) return { bg: 'rgba(74,222,128,0.18)', fg: '#4ade80' }
  if (score >= 45) return { bg: 'rgba(251,191,36,0.18)', fg: '#fbbf24' }
  return { bg: 'rgba(248,113,113,0.18)', fg: '#f87171' }
}

export default function AdvisorCard({ data, loading }) {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()

  // Réutilise l'agrégat déjà chargé par le dashboard : aucun appel réseau
  // supplémentaire. Repli silencieux si l'agrégat n'est pas encore disponible.
  const advisor = React.useMemo(() => {
    try {
      return buildSocialAdvisor({ dashboard: data || {}, user: {}, t })
    } catch (_) {
      return null
    }
  }, [data, t])

  const score = advisor?.socialScore?.global ?? null
  const chip = scoreChipColors(score)
  const lead =
    (advisor?.recommendation && advisor.recommendation.trim()) ||
    (advisor?.recommendations && advisor.recommendations[0]) ||
    ''

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <CardHeader
        icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
        title={t('conseillerSocial.card.title', 'Conseiller relationnel')}
        subtitle={t('conseillerSocial.card.subtitle', 'Votre score & vos recommandations')}
        actions={
          score != null ? (
            <Chip
              label={t('conseillerSocial.scoreChip', {
                score,
                defaultValue: `Score ${score}/100`,
              })}
              size="small"
              sx={{
                bgcolor: chip.bg,
                color: chip.fg,
                fontWeight: 700,
                height: 22,
              }}
            />
          ) : null
        }
      />

      <Box mt={2} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
            {t('conseillerSocial.card.loading', 'Analyse de votre vie relationnelle…')}
          </Box>
        ) : lead ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${SOCIAL_ACCENT}18, transparent 70%)`,
              border: `1px solid ${SOCIAL_ACCENT}30`,
              color: 'rgba(255,255,255,0.9)',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {lead}
          </Box>
        ) : (
          <Box sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.5 }}>
            {t(
              'conseillerSocial.card.empty',
              'Ajoutez vos contacts et notez les plus importants : votre conseiller relationnel calcule alors un score et des recommandations personnalisées.',
            )}
          </Box>
        )}
      </Box>

      <Button
        fullWidth
        data-noclick="true"
        onClick={() => navigate('/conseiller-social')}
        startIcon={<OpenInFullIcon />}
        sx={{
          mt: 2,
          color: '#f7c8e0',
          fontWeight: 600,
          textTransform: 'none',
          border: `1px solid ${SOCIAL_ACCENT}66`,
          bgcolor: `${SOCIAL_ACCENT}29`,
          py: 0.75,
          '&:hover': { bgcolor: `${SOCIAL_ACCENT}3d`, borderColor: SOCIAL_ACCENT },
        }}
      >
        {t('conseillerSocial.viewFullAnalysis', "Voir l'analyse complète")}
      </Button>
    </Box>
  )
}
